import { cn } from '@/utils/styles';
import { NavBar } from '@design-system/NavBar';
import { StandardContainer } from '@design-system/StandardContainer';
import { ChevronRight } from 'lucide-react';
import { Button } from '@design-system/Button';
import { useNavigate } from 'react-router-dom';
import { HOME_PATH } from '@/constants/paths';
import { ErrorLoadingIcon } from '@/components/icons/ErrorLoadingIcon';
import { getBackgroundImageUrl } from '@utils/nutrition/getImageUrl';
import useLogout from '@hooks/useLogout';
import { useIframeModeStore } from '@hooks/useIframeModeStore';

type ErrorVariant = 'error' | 'notFound' | 'offline' | 'logout';

interface ErrorLoadingPageProps {
  variant?: 'light' | 'dark';
  errorType?: ErrorVariant;
  description?: string;
  hideNavBar?: boolean;
  hideBackground?: boolean;
}

const ERROR_CONFIGS = {
  error: {
    description: 'Something went wrong.\nPlease try again later.',
    showButton: true,
    buttonText: 'Try Again',
  },
  notFound: {
    description: "The page was not found.",
    showButton: true,
    buttonText: 'Home',
  },
  offline: {
    description: 'Please check your Internet connection or try again later.',
    showButton: true,
    buttonText: 'Try Again',
  },
  logout: {
    description: 'Something went wrong.\nPlease try again later.',
    showButton: true,
    buttonText: 'Try Again',
  },
};

export const ErrorLoadingPage = ({
  variant = 'light',
  description = '',
  hideNavBar = false,
  errorType = 'error',
  hideBackground = false,
}: ErrorLoadingPageProps) => {
  const navigate = useNavigate();
  const config = ERROR_CONFIGS[errorType];
  const finalDescription = description || config.description;
  const isIframeMode = useIframeModeStore((state) => state.isIframeMode);

  const handleLogout = useLogout();

  const handleButtonClick = async () => {
    if (errorType === 'notFound') {
      navigate(HOME_PATH);
    } else if (errorType === 'logout' && !isIframeMode) {
      await handleLogout();
    } else {
      window.location.reload();
    }
  };

  return (
    <div
      className={cn(
        'min-h-screen',
        variant === 'light' ? hideBackground ? '' : 'bg-white' : 'bg-black',
      )}
    >
      <StandardContainer>
        {!hideNavBar && (
          <NavBar returnButtonTitle="Back" className="md:-ml-5 mt-12" />
        )}

        <div className="h-[calc(100vh-120px)] flex flex-col items-center justify-center">
          { variant === 'light' ? (
            <ErrorLoadingIcon className='mb-4' />
          ) : (
            <img
              className="w-16 h-16 mb-4"
              src={getBackgroundImageUrl('error_loading_dark.png')}
              alt="Error"
              loading="lazy"
            />
          )}
          <div className="text-center">
            <p
              className={cn(
                'typography-text-secondary',
                variant === 'light' ? 'text-primary' : 'text-gray-400',
              )}
            >
              {finalDescription}
            </p>
            {config.showButton && (
              <Button
                onClick={handleButtonClick}
                className={cn(
                  'bg-transparent text-secondary',
                  variant === 'light' ? 'text-secondary' : 'text-gray-400'
                )}
              >
                {config.buttonText}
                <ChevronRight className="w-4 h-4 " />
              </Button>
            )}
          </div>
        </div>
      </StandardContainer>
    </div>
  );
};